/**
 * 宿主页面上下文集（SDK 侧）。
 *
 * 约束来源：CONTRACT.md 第 30/31 行与需求第五节：
 * - 版本是单调十进制（不是 collectedAt），旧版本上报必须被丢弃；
 * - key(conversationId, customerId, pageInstanceId)：每个页面实例独立一份，
 *   多标签页绝不共享单一全局 latestContext；
 * - 发送时必须**原子捕获快照**并在重试时复用同一快照，避免"发完又切了账套"导致上下文错配；
 * - 浏览器不可断言"账套已核实"：客户端全部标记 clientReported=true、companyVerified=false，
 *   未核实的账套/账期显式为 null（绝不沿用旧值）；
 * - 账期/版本/页面标识非法时**明确拒绝**（抛 TypeError），绝不静默当成"未填写"：
 *   静默省略会让调用方以为宿主上报的数据是干净可用的。
 */

import { newPageInstanceId, nextVersion } from '@support/platform';

import type { SupportPageContext, SupportPageContextFields } from './protocol';
import {
  isApprovedFilterKey,
  isNonZeroUint64Decimal,
  isPagePeriod,
  isStablePageId,
} from './protocol';

export interface PageContextStateOptions {
  /** 客户端时钟（ISO8601），仅用于展示/节流，不参与先后判定。 */
  clock?: () => string;
  fields: SupportPageContextFields;
  now?: () => number;
  pageInstanceId?: string;
  /**
   * 初始版本（可选）。宿主已经维护了该 pageInstanceId 的单调版本时用它对齐，
   * 避免同一个页面实例出现两套版本计数（服务端按版本单调判定是否接受上报）。
   */
  version?: string;
}

/** 可空字段：未核实/未选择时显式为 null，绝不能沿用上一页残留值。 */
const NULLABLE_KEYS = [
  'companyId',
  'lastErrorCode',
  'period',
  'selectedObjectId',
  'traceId',
] as const;

void NULLABLE_KEYS;

export class PageContextState {
  readonly pageInstanceId: string;

  private readonly clock: () => string;

  private collectedAt: string;

  private fields: Partial<SupportPageContextFields>;

  private versionValue: string;

  constructor(options: PageContextStateOptions) {
    this.clock = options.clock ?? (() => new Date().toISOString());
    const pageInstanceId = options.pageInstanceId ?? newPageInstanceId();
    if (!isStablePageId(pageInstanceId)) {
      // 实例标识必须是稳定标识（uuid 是其一种），URL/空白会让服务端的实例隔离失效。
      // 错误文本只报固定字段名与错误类别：绝不拼接宿主输入值（可能是任意秘密）。
      throw new TypeError('pageInstanceId 必须是稳定标识（字母/数字/_.:-，<= 100）');
    }
    this.pageInstanceId = pageInstanceId;
    const fields = sanitizeFields(options.fields);
    if (typeof fields.pageKey !== 'string' || fields.pageKey === '') {
      // 不静默兜底成空 pageKey：没有页面标识的上下文无法用于定位问题
      throw new TypeError('页面上下文必须提供非空 pageKey');
    }
    this.fields = fields;
    this.collectedAt = this.clock();
    if (options.version === undefined) {
      // 初始状态即版本 1：调用方拿到对象时就已经有一个可上报的版本。
      this.versionValue = '1';
    } else if (!isNonZeroUint64Decimal(options.version)) {
      // 不放宽成 1：非法版本静默兜底会让服务端的先后判定失去意义。
      // 不拼接版本原值：该字段可能来自接入方误传的任意文本。
      throw new TypeError('页面上下文版本必须是非零规范十进制字符串（<= uint64）');
    } else {
      this.versionValue = options.version;
    }
  }

  get version(): string {
    return this.versionValue;
  }

  /** 当前最新状态（每次调用返回新对象，调用方改不动内部状态）。 */
  get latest(): SupportPageContext {
    return this.materialize();
  }

  /**
   * 合并一批字段并推进版本。
   * 版本用 nextVersion()（BigInt 自增），绝不使用时间戳。
   * 字段或版本非法时抛错，且**不提交半成品状态**（先校验、后落位）。
   */
  update(partial: Partial<SupportPageContextFields>): SupportPageContext {
    const nextFields = mergeFields(this.fields, sanitizeFields(partial));
    const nextVersionValue = this.advanceVersion();
    this.fields = nextFields;
    this.versionValue = nextVersionValue;
    this.collectedAt = this.clock();
    return this.materialize();
  }

  /**
   * 身份切换 / 页面重置：丢弃与旧身份绑定的页面状态。
   * 注意只重置字段本身，**版本继续单调前进**（服务端永远看不到版本回退）。
   */
  reset(partial: Partial<SupportPageContextFields>): SupportPageContext {
    const next = sanitizeFields(partial);
    const nextFields: Partial<SupportPageContextFields> = {
      ...next,
      companyId: next.companyId ?? null,
      pageKey: next.pageKey ?? this.fields.pageKey,
      period: next.period ?? null,
    };
    const nextVersionValue = this.advanceVersion();
    this.fields = nextFields;
    this.versionValue = nextVersionValue;
    this.collectedAt = this.clock();
    return this.materialize();
  }

  /**
   * 发送时快照：深拷贝 + 冻结语义（调用方持有独立副本）。
   * 与 latest 完全分离——之后任何 update 都不会改变已捕获的快照。
   */
  captureSnapshot(): SupportPageContext {
    return this.materialize();
  }

  /**
   * 版本自增；越界（> uint64）时直接拒绝。
   * 服务端用 ParseUint(...,64) 判定，继续投递只会得到 1901003，
   * 而且会让"版本单调"这条判定依据悄悄失真。
   */
  private advanceVersion(): string {
    const next = nextVersion(this.versionValue);
    if (!isNonZeroUint64Decimal(next)) {
      throw new TypeError('页面上下文版本超出 uint64 上限');
    }
    return next;
  }

  private materialize(): SupportPageContext {
    const context: SupportPageContext = {
      clientReported: true,
      collectedAt: this.collectedAt,
      // 浏览器侧永不自称"已核实"：核实只能来自服务端权威来源。
      companyVerified: false,
      companyId: this.fields.companyId ?? null,
      pageInstanceId: this.pageInstanceId,
      pageKey: this.fields.pageKey ?? '',
      period: this.fields.period ?? null,
      version: this.versionValue,
    };
    if (this.fields.companyId === undefined) context.companyId = null;
    const extra = context as unknown as Record<string, unknown>;
    for (const key of [
      'frontendVersion',
      'lastErrorCode',
      'module',
      'pageTitle',
      'product',
      'routeName',
      'selectedObjectId',
      'tab',
      'traceId',
    ] as const) {
      const value = this.fields[key];
      if (value === undefined) continue;
      extra[key] = value;
    }
    if (this.fields.filters !== undefined) {
      context.filters = { ...this.fields.filters };
    }
    return context;
  }
}

/**
 * 丢弃空串、非字符串筛选值与不可序列化的可疑字段，避免把任意对象塞进上下文。
 *
 * 硬性口径（每条都会在非法时抛错，而不是静默丢弃）：
 * - `filters` 只保留后端 approvedFilters 白名单键（period/status/keyword/type/module），
 *   凭据样式键（token/password/secret/cookie/authorization）与未批准键在出站前就剔除；
 * - `period` 必须是 [12]xxx 年 + 合法月（YYYY-MM），null 表示未核实；非法值抛错；
 * - `pageKey` 必须是稳定标识（URL/空白不是标识）；
 * - `routeName` 非空时必须与 Go stableID 同口径（稳定标识）；空值 = 未提供。
 */
function sanitizeFields(
  input: Partial<SupportPageContextFields>,
): Partial<SupportPageContextFields> {
  const out: Partial<SupportPageContextFields> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined) continue;
    if (key === 'filters') {
      const filters: Record<string, string> = {};
      for (const [fk, fv] of Object.entries(
        (value ?? {}) as Record<string, unknown>,
      )) {
        // 白名单之外（含凭据样式键）一律不写入：绝不依赖后端收到后再过滤
        if (!isApprovedFilterKey(fk)) continue;
        if (typeof fv === 'string' && fv !== '') filters[fk] = fv;
      }
      if (Object.keys(filters).length > 0) out.filters = filters;
      continue;
    }
    if (key === 'companyVerified') continue; // 浏览器不可自行断言
    if (key === 'pageKey') {
      if (value === null || value === '') continue; // 未提供：保持既有页面标识
      if (!isStablePageId(value)) {
        throw new TypeError('页面稳定标识必须是标识（字母/数字/_.:-，<= 100）');
      }
      out.pageKey = value;
      continue;
    }
    if (key === 'routeName') {
      // 空值 = 未提供（省略字段）；非空必须与 Go stableID 相同
      // （contextx/context.go:48-50：非空 routeName 不是标识即「页面路由标识错误」）。
      // 绝不改写字符串蒙混过校验：接入方误把 fullPath/查询串当 routeName 时当场拒绝。
      if (value === null || value === '') continue;
      if (!isStablePageId(value)) {
        throw new TypeError('页面路由标识必须是稳定标识（字母/数字/_.:-，<= 100）');
      }
      out.routeName = value;
      continue;
    }
    if (key === 'period') {
      if (value === null) {
        out.period = null; // 显式未核实
        continue;
      }
      if (typeof value === 'string' && value === '') continue; // 空串 = 未提供
      if (!isPagePeriod(value)) {
        // 非法账期绝不当成"没填"：静默省略会让调用方以为宿主的数据是干净的。
        // 错误文本不拼接账期原值（可能是任意文本）。
        throw new TypeError('账期必须是 [12]xxx 年 + 合法月（YYYY-MM，例如 2026-09）');
      }
      out.period = value;
      continue;
    }
    if (value === null) {
      // 只有协议允许为空的字段可以显式为 null
      if (key === 'companyId') {
        out.companyId = null;
      }
      continue;
    }
    // 空串等同于"未提供"：绝不写入上下文，避免把空页面标题/空账期当成有意义的状态
    if (typeof value === 'string' && value === '') continue;
    (out as Record<string, unknown>)[key] = value;
  }
  return out;
}

function mergeFields(
  base: Partial<SupportPageContextFields>,
  partial: Partial<SupportPageContextFields>,
): Partial<SupportPageContextFields> {
  const merged: Partial<SupportPageContextFields> = { ...base };
  for (const [key, value] of Object.entries(partial)) {
    if (value === undefined) continue;
    (merged as Record<string, unknown>)[key] = value;
  }
  return merged;
}

export { NULLABLE_KEYS };